import React, { useRef, useImperativeHandle } from 'react'
import { useForm } from 'react-hook-form'
import Snackbar from '@mui/material/Snackbar'
import MenuItem from '@mui/material/MenuItem'
import CountrySelect from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'

import Input from '../shared/Input'
import Select from '../shared/Select'
import Date from '../shared/Date'
import MaskedInput from '../shared/Masked'
import * as normalizers from '../../utils/normalizers'
import * as validators from '../../utils/validators'
import { Span } from './styles/EditUserForm.style'

const zipcodeUrl = zipcode => `https://viacep.com.br/ws/${zipcode}/json/`

const buildDefaultValues = initialValues => ({
  fractal_id: initialValues.fractal_id,
  name: initialValues.name,
  email: initialValues.email,
  genre: initialValues.genre,
  birthday: initialValues.birthday,
  phone: initialValues.phone,
  taxpayer_number: initialValues.taxpayer_number,
  additional_data: initialValues.additional_data
})

const isDirty = (current, initial) => Object.keys(initial)
  .some(key => current[key] !== initial[key])

const EditUserForm = ({
  onSubmit,
  initialValues,
  translation,
  lang,
  formRef
}) => {
  const [open, setOpen] = React.useState(false)
  const [country, setCountry] = React.useState('')
  const [snackbarMessage, setSnackbarMessage] = React.useState('')
  const fields = ['name', 'email', 'birthday', 'fractal_id', 'taxpayer_number', 'genre', 'phone', 'address']
  const addressArray = [
    'zipcode',
    'street',
    'number',
    'complement',
    'neighborhood',
    'city',
    'state'
  ]
  const inputComponent = { Input, Select, Date, MaskedInput }
  const required = lang === 'pt-BR' ? validators.requiredPT : validators.requiredES
  const phoneValidation = country === 'BRA' ? validators.phoneValidationBR : validators.phoneValidationPAR
  const normalizePhone = country === 'BRA' ? normalizers.normalizePhoneBRA : normalizers.normalizePhonePAR

  const initialValuesRef = useRef(initialValues)
  const defaultValues = buildDefaultValues(initialValuesRef.current)
  const { control, watch, getValues, setValue, handleSubmit } = useForm({ defaultValues })
  const zipcode = watch('additional_data.address.zipcode')

  useImperativeHandle(formRef, () => ({
    submit: () => handleSubmit(onSubmit)(),
    isDirty: () => isDirty(getValues(), defaultValues)
  }))

  const options = {
    name: {
      type: 'text',
      name: 'name',
      label: `${translation('editUserForm')['nameLabel']}:`,
      disabled: false,
      component: 'Input',
      validate: [required]
    },
    email: {
      type: 'text',
      name: 'email',
      label: `${translation('editUserForm')['emailLabel']}:`,
      disabled: true,
      component: 'Input'
    },
    birthday: {
      type: 'date',
      name: 'birthday',
      label: `${translation('editUserForm')['birthdayLabel']}:`,
      disabled: false,
      component: 'Date',
      validate: [required]
    },
    phone: {
      type: 'text',
      name: 'phone',
      label: `${translation('editUserForm')['phoneLabel']}:`,
      disabled: false,
      component: 'MaskedInput',
      normalize: normalizePhone,
      validate: [required, phoneValidation],
      numberFormat: country === 'BRA' ? '(##) #####-####' : country === 'PAR' ? '##########' : '',
      prefix: translation('editUserForm')['phonePrefix']
    },
    taxpayer_number: {
      type: 'text',
      name: 'taxpayer_number',
      label: country === 'BRA' ? 'CPF' : 'CI',
      disabled: !!initialValues.taxpayer_number,
      component: 'MaskedInput',
      normalize: normalizers.normalizeTaxPayer,
      validate: [required, validators.taxpayerValidation],
      numberFormat: country === 'BRA' ? '###.###.###-##' : country === 'PAR' ? '#######' : ''

    },
    genre: {
      type: 'select',
      name: 'genre',
      label: `${translation('editUserForm')['genreLabel']}:`,
      disabled: false,
      component: 'Select',
      options: [
        { label: 'Masculino', value: 'male' },
        { label: 'Feminino', value: 'female' }
      ],
      validate: [required]
    },
    fractal_id: {
      type: 'text',
      name: 'fractal_id',
      label: 'Fractal Id',
      disabled: true,
      component: 'MaskedInput'
    },
    zipcode: {
      type: 'text',
      name: 'zipcode',
      label: `${translation('editUserForm')['zipcodeLabel']}:`,
      disabled: false,
      component: 'MaskedInput',
      numberFormat: '#####-###'
    },
    street: {
      type: 'text',
      name: 'street',
      label: `${translation('editUserForm')['streetLabel']}:`,
      disabled: false,
      component: 'Input'
    },
    number: {
      type: 'number',
      name: 'number',
      label: `${translation('editUserForm')['numberLabel']}:`,
      disabled: false,
      component: 'Input'
    },
    complement: {
      type: 'other',
      name: 'complement',
      label: `${translation('editUserForm')['complementLabel']}:`,
      disabled: false,
      component: 'Input'
    },
    neighborhood: {
      type: 'text',
      name: 'neighborhood',
      label: `${translation('editUserForm')['neighborhoodLabel']}:`,
      disabled: false,
      component: 'Input'
    },
    city: {
      type: 'text',
      name: 'city',
      label: `${translation('editUserForm')['cityLabel']}:`,
      disabled: false,
      component: 'Input'
    },
    state: {
      type: 'text',
      name: 'state',
      label: `${translation('editUserForm')['stateLabel']}:`,
      disabled: false,
      component: 'Input'
    }
  }
  const handleClose = () => {
    setOpen(false)
  }
  const searchZipcopde = () => {
    const newZipcode = zipcode?.replace(/[^0-9]/g, '')
    if (newZipcode?.length !== 8) {
      setSnackbarMessage('O cep deve possuir 8 números')
      setOpen(!open)
      return
    }
    fetch(zipcodeUrl(newZipcode))
      .then(res => res.json())
      .then(data => {
        if (data.erro) {
          setOpen(!open)
          setSnackbarMessage(`O cep ${zipcode} não foi encontrado`)
        } else {
          setValue('additional_data.address.street', data.logradouro, { shouldDirty: true })
          setValue('additional_data.address.neighborhood', data.bairro, { shouldDirty: true })
          setValue('additional_data.address.city', data.localidade, { shouldDirty: true })
          setValue('additional_data.address.state', data.uf, { shouldDirty: true })
        }
      })
      .catch(error => console.error(error))
  }

  const fieldRules = element => {
    const { validate } = options[element]
    if (!validate) {
      return undefined
    }
    return { validate: validators.composeValidators(...validate) }
  }

  const renderField = (element, name) => {
    const option = options[element]
    const Component = inputComponent[option.component]
    const commonProps = {
      control,
      name,
      rules: fieldRules(element),
      label: option.label,
      placeholder: option.label,
      disabled: option.disabled,
      id: option.name
    }

    if (Component === Select) {
      return <Select key={name} {...commonProps} options={option.options || []} />
    }

    if (Component === Date) {
      return <Date key={name} {...commonProps} />
    }

    if (Component === MaskedInput) {
      return (
        <MaskedInput
          key={name}
          {...commonProps}
          normalizers={option.normalize ? [option.normalize] : undefined}
          numberFormat={option.numberFormat}
          prefix={option.prefix}
        />
      )
    }

    return <Input key={name} {...commonProps} type={option.type} />
  }

  const renderForm = fields.map((element) => {
    if (element === 'address') {
      return addressArray.map(address => (
        <React.Fragment key={`address[${address}]`}>
          {renderField(address, `additional_data.address.${options[address].name}`)}
          {
            options[address].name === 'zipcode' && country === 'BRA' &&
            <Span
              onClick={() => searchZipcopde()}
            >
              Buscar
            </Span>
          }
        </React.Fragment>
      ))
    }

    return renderField(element, options[element].name)
  })

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      padding: '5%',
      width: '100%'
    }}>
      { country === 'BRA' || country === 'PAR' ? (<h2>{translation('editUserForm')['personalInformation']}</h2>) : (<></>)}

      <InputLabel id='country-label'> {translation('editUserForm')['selectCountry']}  </InputLabel>
      <CountrySelect
        labelId='country-label'
        id='country'
        value={country}
        onChange={event => setCountry(event.target.value)}
      >
        {[
          {
            label: 'Brasil',
            value: 'BRA'
          },
          {
            label: 'Paraguay',
            value: 'PAR'
          }
        ].map(country => {
          return (
            <MenuItem key={country.value} value={country.value}> {country.label} </MenuItem>
          )
        })}
      </CountrySelect>
      <form style={{ display: 'flex', flexDirection: 'column' }} onSubmit={handleSubmit(onSubmit)}>
        {country !== '' && renderForm }
      </form>
      <Snackbar
        open={open}
        onClose={handleClose}
        message={snackbarMessage}
      />
    </div>
  )
}

export default EditUserForm
